import React, { useState, useRef, useEffect } from 'react';
import { Select as AntSelect } from 'antd';
import Flag from '../Flag/Flag';
import { t } from '../../i18n/i18n';
import { COUNTRIES, DEFAULT_COUNTRY_CODE, findCountry } from '../../utilities/country';
import './Select.scss';

const icon = (name) => ({ '--icon': `url(/icons/${name}.svg)` });

/**
 * How well a country name answers a typed query — lower is better, `null` is no
 * match. The start of the name beats the start of a later word, which beats
 * anywhere inside it, so "ind" puts India ahead of British Indian Ocean
 * Territory and the first letters of a country land on that country.
 */
const nameRank = (name, query) => {
  const lower = name.toLowerCase();
  if (!query) return 0;
  if (lower.startsWith(query)) return 0;
  if (lower.split(/[\s,()-]+/).some((word) => word.startsWith(query))) return 1;
  if (lower.includes(query)) return 2;
  return null;
};

/**
 * A country's name as the reader searches for it: in their language and in
 * English (src/locales/<lang>/countries.json), so "Allemagne" and "Germany"
 * both find it. The value sent on is still the code.
 */
const searchName = (c) => {
  const local = t(c.name);
  return local === c.name ? c.name : `${local} / ${c.name}`;
};

const bestCountry = (input, countries = COUNTRIES) => {
  const query = input.trim().toLowerCase();
  if (!query) return null;
  let best = null;
  let bestRank = Infinity;
  countries.forEach((c) => {
    const rank = nameRank(searchName(c), query);
    if (rank !== null && rank < bestRank) {
      best = c;
      bestRank = rank;
    }
  });
  return best;
};

export default function Select({
  label,
  placeholder,
  value,
  onChange,
  name,
  error,
  disabled,
  options,
  // The country list to offer when `options` isn't given — every country by
  // default; sign-up passes only the ones we serve.
  countries = COUNTRIES,
  required,
  variant = 'default',
  className = '',
  ...props
}) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);
  // antd drops typed search text on blur in single mode, so "Portu" then a
  // click elsewhere used to snap back to the old country. Keep it to commit it.
  const [search, setSearch] = useState('');

  // Handle outside clicks to close the custom dropdown
  useEffect(() => {
    function handleClickOutside(event) {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (variant === 'form') {
    const nativeOptions = options || [];
    // Find label for current value
    const selectedOption = nativeOptions.find((opt) => {
      const val = typeof opt === 'object' ? opt.value : opt;
      return String(val) === String(value);
    });
    const displayLabel = selectedOption
      ? (typeof selectedOption === 'object' ? selectedOption.label : selectedOption)
      : placeholder;

    const handleSelect = (val) => {
      if (onChange) {
        onChange({ target: { name, value: val } });
      }
      setIsOpen(false);
    };

    return (
      <div
        className={`reusable-input-group reusable-input-group--form ${error ? 'has-error' : ''} ${disabled ? 'is-disabled' : ''} ${className}`}
        ref={containerRef}
      >
        {label && (
          <label className="reusable-input-label">
            {t(label)}
            {required && <span className="reusable-input-required" style={{ color: '#fd6b74', marginLeft: '3px' }}>*</span>}
          </label>
        )}
        {/* `ac-menu-open` lets a `page-stack` section lift itself over the
            sections after it while this menu hangs out of it — see the mixin. */}
        <div className={`reusable-custom-select-wrapper ${isOpen ? 'is-open ac-menu-open' : ''}`}>
          <button
            type="button"
            className={`reusable-custom-select-trigger ${!value ? 'is-placeholder' : ''}`}
            onClick={() => !disabled && setIsOpen(!isOpen)}
            disabled={disabled}
          >
            <span className="reusable-custom-select-trigger-label">{t(displayLabel)}</span>
            <span className="icon reusable-custom-select-chevron" style={icon('chevron-down')} />
          </button>

          {isOpen && (
            <div className="reusable-custom-select-menu">
              {nativeOptions.map((opt) => {
                const val = typeof opt === 'object' ? opt.value : opt;
                const lbl = typeof opt === 'object' ? opt.label : opt;
                const isSelected = String(val) === String(value);
                return (
                  <button
                    key={val}
                    type="button"
                    className={`reusable-custom-select-item ${isSelected ? 'is-selected' : ''}`}
                    onClick={() => handleSelect(val)}
                  >
                    <span>{t(lbl)}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
        {error && <span className="reusable-input-error-text">{t(error)}</span>}
      </div>
    );
  }

  // Resolve selected country
  const selectedCountry = findCountry(value) || findCountry(DEFAULT_COUNTRY_CODE);

  // Render option custom labels inside Select components
  const renderCountryLabel = (c) => (
    <span className="country-select-option-item" style={{ display: 'flex', alignItems: 'center', gap: '8px', height: '100%' }}>
      <Flag code={c.code} className="country-select-flag-img" />
      <span className="country-select-name" style={{ color: 'var(--text-primary, #f7f8f8)' }}>{t(c.name)}</span>
    </span>
  );

  const selectOptions = options?.map((opt) =>
    opt && typeof opt.label === 'string' ? { ...opt, label: t(opt.label) } : opt
  ) || countries.map((c) => ({
    value: c.code,
    label: renderCountryLabel(c),
    // The label is JSX, so antd has nothing to match a typed query against —
    // carry the plain name for `filterOption` below — in both languages.
    name: searchName(c),
  }));

  const emitChange = (val) => {
    setSearch('');
    if (onChange) {
      onChange({ target: { name, value: val } });
    }
  };

  return (
    <div className={`reusable-input-group ${error ? 'has-error' : ''} ${disabled ? 'is-disabled' : ''} ${className}`}>
      {label && (
        <label className="reusable-input-label">
          {t(label)}
          {required && <span className="reusable-input-required">*</span>}
        </label>
      )}

      <AntSelect
        className="reusable-antd-select"
        classNames={{ popup: { root: 'reusable-antd-select-popup' } }}
        value={value !== undefined ? value : selectedCountry.code}
        onChange={emitChange}
        onBlur={() => {
          const match = !options && bestCountry(search, countries);
          if (match && match.code !== value) emitChange(match.code);
          else setSearch('');
        }}
        disabled={disabled}
        status={error ? 'error' : ''}
        placeholder={t(placeholder)}
        options={selectOptions}
        // 249 countries is too many to scroll, so let the user type.
        showSearch={{
          onSearch: setSearch,
          filterOption: (input, option) =>
            nameRank(option?.name ?? '', input.trim().toLowerCase()) !== null,
          filterSort: (a, b, { searchValue }) => {
            const query = searchValue.trim().toLowerCase();
            return nameRank(a.name ?? '', query) - nameRank(b.name ?? '', query);
          },
        }}
        {...props}
      />

      {error && <span className="reusable-input-error-text">{t(error)}</span>}
    </div>
  );
}
